import { useEffect, useState, useCallback, useRef } from "react";
import { getPaginatedFileProcessing } from "../services/fileProcessingService";

interface IUseFetchFileProcessing {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: number;
  pSize: number;
  searchTerm?: string;
  statusFilter?: string;
}

const useFetchPaginatedFileProcessing = ({
  setIsLoading,
  page,
  pSize,
  searchTerm,
  statusFilter = "Active",
}: IUseFetchFileProcessing) => {
  const [fileProcessingData, setFileProcessingData] = useState<any>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Map statusFilter to isActive for API call
  const isActive =
    statusFilter === "Active"
      ? "true"
      : statusFilter === "Inactive"
      ? "false"
      : "all";

  const fetchData = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result = await getPaginatedFileProcessing({
          page,
          pSize,
          searchTerm: searchQuery || searchTerm,
          isActive,
          signal: abortControllerRef.current.signal,
        });
        setFileProcessingData(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setFileProcessingData([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, pSize, searchTerm, isActive, setIsLoading]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [fileProcessingData, fetchData];
};

export default useFetchPaginatedFileProcessing;
